/**
 * Copyright IBM Corp. 2022, 2026
 */

/**
 * DUPLICATE FILE
 *
 * Ejected from @culturehq/add-to-calendar/src/makeUrls.ts:
 * https://github.com/CultureHQ/add-to-calendar/blob/master/src/makeUrls.ts
 */

type Details =
	| {
			plainText: string
			html: string
	  }
	| string
	| null
export interface CalendarEvent {
	name: string
	details: Details
	location: string | null
	startsAt: string
	endsAt: string
}

const makeDuration = (event: CalendarEvent) => {
	const minutes = Math.floor(
		(+new Date(event.endsAt) - +new Date(event.startsAt)) / 60 / 1000,
	)
	return `${`0${Math.floor(minutes / 60)}`.slice(-2)}${`0${minutes % 60}`.slice(
		-2,
	)}`
}

const makeTime = (time: string) =>
	new Date(time).toISOString().replace(/[-:]|\.\d{3}/g, '')

type Query = { [key: string]: null | boolean | number | string }

const makeUrl = (base: string, query: Query) =>
	Object.keys(query).reduce((accum, key, index) => {
		const value = query[key]

		if (value !== null) {
			return `${accum}${index === 0 ? '?' : '&'}${key}=${encodeURIComponent(
				value,
			)}`
		}
		return accum
	}, base)

const makeDetails = (details: Details, calendarType: 'html' | 'plainText') => {
	if (!details) {
		return ''
	}

	if (typeof details === 'string') {
		return details
	}

	if (calendarType === 'html') {
		return details.html
	}

	return details.plainText
}

const makeGoogleCalendarUrl = (event: CalendarEvent) =>
	makeUrl('https://calendar.google.com/calendar/render', {
		action: 'TEMPLATE',
		dates: `${makeTime(event.startsAt)}/${makeTime(event.endsAt)}`,
		location: event.location,
		text: event.name,
		details: makeDetails(event.details, 'html'),
	})

const makeOutlookCalendarUrl = (
	event: CalendarEvent,
	variant?: 'default' | 'business',
) => {
	const urls = {
		default: 'https://outlook.live.com/owa',
		business: 'https://outlook.office.com/calendar/0/deeplink/compose',
	}
	const url = urls[variant]
	return makeUrl(url, {
		rru: 'addevent',
		startdt: event.startsAt,
		enddt: event.endsAt,
		subject: event.name,
		location: event.location,
		body: makeDetails(event.details, 'html'),
		allday: false,
		uid: new Date().getTime().toString(),
		path: '/calendar/view/Month',
	})
}

const makeYahooCalendarUrl = (event: CalendarEvent) =>
	makeUrl('https://calendar.yahoo.com', {
		v: 60,
		view: 'd',
		type: 20,
		title: event.name,
		st: makeTime(event.startsAt),
		dur: makeDuration(event),
		desc: makeDetails(event.details, 'html'),
		in_loc: event.location,
	})

const makeICSCalendarUrl = (event: CalendarEvent) => {
	const components = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'BEGIN:VEVENT']

	// In case of SSR, document won't be defined
	if (typeof document !== 'undefined') {
		components.push(`URL:${document.URL}`)
	}

	components.push(
		`DTSTART:${makeTime(event.startsAt)}`,
		`DTEND:${makeTime(event.endsAt)}`,
		`SUMMARY:${event.name}`,
		`DESCRIPTION:${makeDetails(event.details, 'plainText')}`,
		`LOCATION:${event.location}`,
		'END:VEVENT',
		'END:VCALENDAR',
	)

	return encodeURI(`data:text/calendar;charset=utf8,${components.join('\n')}`)
}

interface Calendar {
	name: string
	calendar: string
}

const makeUrls = (event: CalendarEvent): Calendar[] => [
	{
		name: 'Google',
		calendar: makeGoogleCalendarUrl(event),
	},
	{
		name: 'Outlook Web App',
		calendar: makeOutlookCalendarUrl(event, 'default'),
	},
	{
		name: 'Outlook Web App (Business)',
		calendar: makeOutlookCalendarUrl(event, 'business'),
	},
	{
		name: 'Yahoo',
		calendar: makeYahooCalendarUrl(event),
	},
	{
		name: 'Apple Calendar',
		calendar: makeICSCalendarUrl(event),
	},
	{
		name: 'Outlook',
		calendar: makeICSCalendarUrl(event),
	},
]

export default makeUrls
